import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Column } from '@ant-design/plots';

const moment = require('moment');

const AttendanceByTimeSheetGraph = ({attendanceOverview, type = 'days', startDate, endDate}) => {
  const raw = attendanceOverview?.attendanceByTimesheet?.attendanceDetails || [];

  const toHours = (mins) => {
    if (mins === null || mins === undefined) return 0;
    return mins / 60;
  }

  const getDateKey = (value) => {
    if (!value) return null;
    const formats = ['YYYY-MM-DD','YYYY-MM-DDTHH:mm:ss','DD-MMM','DD/MM/YYYY','YYYY/MM/DD','MM/DD/YYYY','DD-MMM-YYYY','D-MMM','D/MM/YYYY'];
    const parsed = moment(value, formats, true);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  }

  let data = [];

  if (type === 'month') {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const map = {};
    raw.forEach(item => {
      let key = '';
      if (item.unit && /[A-Za-z]{3}/.test(item.unit)) key = item.unit.slice(0,3);
      else if (item.date) key = moment(item.date).format('MMM');
      else {
        // try parse unit as date
        const m = moment(item.unit, ['DD-MMM','YYYY-MM-DD','DD/MM/YYYY','MM/DD/YYYY'], true);
        key = m.isValid() ? m.format('MMM') : item.unit;
        key = key ? key.slice(0,3) : item.unit;
      }
      map[key] = map[key] || {duration:0,clockIn:null,clockOut:null,workingHrs:null};
      map[key].duration += toHours(item.durationInMinutes);
      map[key].clockIn = map[key].clockIn || item.clockIn;
      map[key].clockOut = map[key].clockOut || item.clockOut;
      map[key].workingHrs = map[key].workingHrs || item.workingHours;
    });
    data = months.map(m => ({ unit: m, durationInHours: map[m]?.duration || 0, clockIn: map[m]?.clockIn || null, clockOut: map[m]?.clockOut || null, workingHrs: map[m]?.workingHrs || null }));
  } else if (type === 'year') {
    const map = {};
    raw.forEach(item => {
      let key = '';
      if (item.unit && /^\d{4}$/.test(item.unit)) key = item.unit;
      else if (item.date) key = moment(item.date).format('YYYY');
      else {
        const m = moment(item.unit, ['DD-MMM','YYYY-MM-DD','DD/MM/YYYY','MM/DD/YYYY'], true);
        key = m.isValid() ? m.format('YYYY') : (item.unit || '');
      }
      map[key] = map[key] || {duration:0,clockIn:null,clockOut:null,workingHrs:null};
      map[key].duration += toHours(item.durationInMinutes);
      map[key].clockIn = map[key].clockIn || item.clockIn;
      map[key].clockOut = map[key].clockOut || item.clockOut;
      map[key].workingHrs = map[key].workingHrs || item.workingHours;
    });
    const years = Object.keys(map).sort();
    data = years.map(y => ({ unit: y, durationInHours: map[y].duration || 0, clockIn: map[y].clockIn || null, clockOut: map[y].clockOut || null, workingHrs: map[y].workingHrs || null }));
  } else {
    const dayMap = {};
    raw.forEach(item => {
      const dateKey = getDateKey(item.date || item.unit || item.day || item.attendanceDate);
      if (!dateKey) return;
      dayMap[dateKey] = dayMap[dateKey] || { durationInHours: 0, clockIn: null, clockOut: null, workingHrs: null };
      dayMap[dateKey].durationInHours += toHours(item.durationInMinutes);
      dayMap[dateKey].clockIn = dayMap[dateKey].clockIn || item.clockIn;
      dayMap[dateKey].clockOut = dayMap[dateKey].clockOut || item.clockOut;
      dayMap[dateKey].workingHrs = dayMap[dateKey].workingHrs || item.workingHours;
    });

    const start = startDate ? moment(startDate) : null;
    const end = endDate ? moment(endDate) : null;

    if (start && end && start.isValid() && end.isValid() && start.isSameOrBefore(end)) {
      let cursor = start.clone();
      while (cursor.isSameOrBefore(end, 'day')) {
        const key = cursor.format('YYYY-MM-DD');
        const record = dayMap[key] || { durationInHours: 0, clockIn: null, clockOut: null, workingHrs: null };
        data.push({
          unit: cursor.format('D'),
          durationInHours: record.durationInHours,
          clockIn: record.clockIn,
          clockOut: record.clockOut,
          workingHrs: record.workingHrs,
        });
        cursor.add(1, 'day');
      }
    } else {
      data = raw.map(item => {
        let unitLabel = item.unit;
        const m = moment(item.unit, ['DD-MMM','YYYY-MM-DD','DD/MM/YYYY','MM/DD/YYYY'], true);
        if (m.isValid()) unitLabel = m.format('D');
        return { unit: unitLabel, durationInHours: toHours(item.durationInMinutes), clockIn: item.clockIn || null, clockOut: item.clockOut || null, workingHrs: item.workingHours || null };
      });
    }
  }

  const config = {
    data,
    xField: "unit",
    yField: "durationInHours",
    color: "#C3B1EC",
    columnStyle: {
      fill: "#C3B1EC",
      radius: [4,4,0,0]
    },
    tooltip: {
      customContent: (val, item) => {
        const d = item && item[0] && item[0].data ? item[0].data : (val && val[0] ? val[0] : {});
        return (
          <div>
            <div style={{ height: "50px", minWidth: "200px" }}>
              <div style={{ width: '220px', display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: "20px" }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: '11px', fontFamily: "Roboto" }}>CLOCK IN</div>
                  <div className='text-heading' style={{ fontSize: '14px', fontFamily: "Roboto", fontWeight: "bold", marginTop: "10px" }}>{d.clockIn !== null && d.clockIn !== undefined ? d.clockIn : "0:00"}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', marginLeft: "10px" }}>
                  <div style={{ fontSize: '11px', fontFamily: "Roboto" }}>CLOCK OUT</div>
                  <div className='text-heading' style={{ fontSize: '14px', fontFamily: "Roboto", fontWeight: "bold", marginTop: "10px" }}>{d.clockOut !== null && d.clockOut !== undefined ? d.clockOut : "0:00"}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', marginLeft: "10px" }}>
                  <div style={{ fontSize: '11px', fontFamily: "Roboto" }}>WORKING HRS</div>
                  <div className='text-heading' style={{ fontSize: '14px', fontFamily: "Roboto", fontWeight: "bold", marginTop: "10px" }}>{d.workingHrs !== null && d.workingHrs !== undefined ? d.workingHrs : "0:00"}</div>
                </div>
              </div>
            </div>
          </div>
        )
      }
    },
    xAxis: {
      label: {
        autoHide: false,
        autoRotate: false,
      },
    },
    yAxis: {
      min: 0,
      max: 12,
      tickInterval: 3,
      label: {
        formatter: (v) => `${v} hrs`
      },
      grid: {
        line: {
          style: {
            stroke: '#E6E6E6',
            lineDash: [4, 4]
          }
        }
      }
    },
    meta: {
      type: {
        alias: "Attendance",
      },
    },
    slider: false,
    minColumnWidth: 20,
    maxColumnWidth: 40,
  };
  return <Column {...config} />;
};


export default AttendanceByTimeSheetGraph;

